import { ExternalLink, MapPin, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GOOGLE_PLACE } from "@/lib/config";

export const GoogleLogo = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.5 3.9-5.5 3.9-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.9 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z" />
    <path fill="#4285F4" d="M21 12.2c0-.6-.1-1.1-.2-1.6H12v3.9h5.5c-.2 1.1-.8 2.1-1.8 2.8l2.9 2.3c1.7-1.6 2.4-3.9 2.4-7.4z" opacity=".9" />
    <path fill="#34A853" d="M12 21.6c2.6 0 4.8-.9 6.4-2.3l-2.9-2.3c-.8.6-1.9 1-3.5 1-3.9 0-5.3-2.6-5.5-3.9H3.5v2.4c1.6 3.1 4.8 5.1 8.5 5.1z" opacity=".9" />
    <path fill="#FBBC05" d="M6.5 14.1c-.3-.8-.4-1.4-.4-2.1s.2-1.4.4-2.1V7.5H3.5C2.8 8.9 2.4 10.4 2.4 12s.4 3.1 1.1 4.5l3-2.4z" opacity=".9" />
  </svg>
);

export const GoogleReviewButton = ({ className = "", testId = "google-review-btn", children }) => (
  <Button asChild className={`rounded-full bg-slate-900 hover:bg-slate-800 text-white ${className}`} data-testid={testId}>
    <a href={GOOGLE_PLACE.reviewUrl} target="_blank" rel="noopener noreferrer">
      <GoogleLogo className="h-4 w-4 mr-2" /> {children || "Escribir reseña en Google"}
    </a>
  </Button>
);

export const GoogleReviewCard = () => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col gap-5" data-testid="google-review-card">
    <div className="flex items-start gap-4">
      <span className="h-12 w-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
        <GoogleLogo className="h-6 w-6" />
      </span>
      <div>
        <p className="font-heading font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
          Reseñas en Google
          <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full" data-testid="google-rating">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {GOOGLE_PLACE.rating.toLocaleString("es-ES")}
          </span>
        </p>
        <p className="text-sm text-slate-600 mt-1">
          Tu reseña en Google ayuda a que más vecinos del Actur nos encuentren en el mapa. ¡Solo te lleva un minuto!
        </p>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 sm:pl-16">
      <GoogleReviewButton />
      <Button asChild variant="outline" className="rounded-full" data-testid="google-maps-link">
        <a href={GOOGLE_PLACE.mapsUrl} target="_blank" rel="noopener noreferrer">
          <MapPin className="h-4 w-4 mr-2" /> Ver en Maps <ExternalLink className="h-3 w-3 ml-1.5 text-slate-400" />
        </a>
      </Button>
    </div>
  </div>
);

export const GoogleReviewDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md rounded-2xl" data-testid="google-review-dialog">
      <DialogHeader>
        <span className="h-12 w-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center mb-2">
          <GoogleLogo className="h-6 w-6" />
        </span>
        <DialogTitle className="font-heading text-xl">¡Gracias por tu reseña!</DialogTitle>
        <DialogDescription>
          ¿Nos la dejas también en Google? Nos ayuda muchísimo a aparecer más arriba en el mapa para otros vecinos.
          Se abre en una pestaña nueva y puedes pegar el mismo comentario.
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col sm:flex-row gap-2 mt-2">
        <GoogleReviewButton className="flex-1 h-11" testId="google-review-dialog-btn">Publicar también en Google</GoogleReviewButton>
        <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full h-11" data-testid="google-review-dialog-skip">
          Ahora no
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
